/**
 * Kashmere design tokens — THE canonical value source (ADR-0009, #246).
 *
 * Every palette value, font family, radius and motion curve the platform uses
 * is defined here and nowhere else. CLAUDE.md's "KASHMERE UI SYSTEM" section is
 * the narrative spec (what the tokens mean, how the controls behave); this file
 * is the value. The two are drift-tested against each other (`tokens.test.ts`),
 * `tokens.css` is generated from here (`tokensCss.ts`), and a guard fails CI if
 * any of these values is restated outside `packages/core/src/design/`
 * (`canon.guard.test.ts`).
 *
 * The aesthetic is 1970s–80s lit instrumentation: navy-midnight panels, gold /
 * teal / pink accents lit from within, red→green status, square corners.
 * Product surfaces consume these directly (`import { palette, fonts } from
 * '@kashmere/core'`). The marketing tier derives from `tokens.css`.
 */

// ─── Palette ────────────────────────────────────────────────────────────────
// Primary leaves only — every hex appears exactly once. Semantic aliases that
// share a value (status red = pink.lit, wheel armed = green, …) are derived
// below by reference so the reverse map hex → token stays unambiguous.

export const palette = {
  /** Page radial `radialTop → surface → page`; `panel` = cards / dock; `input` = fields. */
  bg: { radialTop: '#0e1a33', surface: '#070d1c', page: '#05080f', panel: '#0a1426', input: '#060c18' },
  /** Gradient stops. Account cards run `accountTop → bg.panel`. */
  card: { accountTop: '#0f1c33', sourceTop: '#12233f', sourceBottom: '#0b1730', sheetTop: '#0e1a30', sheetBottom: '#080f1f' },
  border: { hairline: '#16243e', mid: '#233a5c', active: '#2a416a' },
  gold: { base: '#c8a45a', bright: '#e0c87a', lit: '#e9cf86' },
  /** `ink` = text on a teal fill (the active mode button). */
  teal: { base: '#13c4b4', lit: '#46e6d4', ink: '#04201d' },
  pink: { base: '#f0185a', lit: '#ff5d73', muted: '#ff7d8e' },
  green: '#54e29a',
  plum: '#9c5485',
  text: { cream: '#f4eed9', primary: '#e2ddd4', secondary: '#b8b2a8', dim: '#7e8aa8', faint: '#5a6a86', ledOff: '#243049' },
  /** Roller chrome: the wheel frame gradient, its border, the digit-cell base, the `dim` digit colour. */
  roller: { dim: '#8aa0c4', frameTop: '#040813', frameBottom: '#0a1322', frameBorder: '#1b2a44', cellBase: '#050a17' },
  /** Valve handwheel: idle rim/hub, the three hub-inner shades, the empty rim, the hub gradient shade. */
  wheel: {
    idleRim: '#3a567f',
    idleHub: '#46587a',
    idleHubInner: '#1c3050',
    emptyRim: '#3a2230',
    emptyHubInner: '#2a1620',
    armedHubInner: '#16402c',
    hubShade: '#0a1424',
  },
  flag: { trustBorder: '#4a3f22', irrevocableBorder: '#3a2435', setAsideBorder: '#1d3a3a' },
  /** Tax-coverage bar track (fills red→green over it). */
  meter: { taxTrack: '#22121a' },
} as const;

export type PaletteHex = string;

/** Flatten the palette to `[dotted.path, hex]` pairs — the reverse map + the CSS renderer both use it. */
export function paletteEntries(): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const walk = (node: unknown, path: string[]): void => {
    if (typeof node === 'string') {
      out.push([path.join('.'), node]);
      return;
    }
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) walk(v, [...path, k]);
  };
  walk(palette, []);
  return out;
}

// ─── Derived colour roles (by reference, never restated) ───────────────────

/** Red = pending / unplanned / empty · Green = armed / ready / funded / full. */
export const status = { red: palette.pink.lit, green: palette.green } as const;

export type RollerColor = 'gold' | 'red' | 'green' | 'teal' | 'cream' | 'dim';

/** The six lit digit colours of the Roller. */
export const rollerPalette: Record<RollerColor, string> = {
  gold: palette.gold.lit,
  red: palette.pink.lit,
  green: palette.green,
  teal: palette.teal.lit,
  cream: palette.text.cream,
  dim: palette.roller.dim,
};

export const flagStyles = {
  trust: { color: palette.gold.base, border: palette.flag.trustBorder },
  irrevocable: { color: palette.plum, border: palette.flag.irrevocableBorder },
  setAside: { color: palette.teal.lit, border: palette.flag.setAsideBorder },
} as const;

/** Valve handwheel states: idle → armed (green, 14px glow) → empty (red, disabled). */
export const wheelStates = {
  idle: { rim: palette.wheel.idleRim, hub: palette.wheel.idleHub, hubInner: palette.wheel.idleHubInner, glow: '2px' },
  armed: { rim: palette.green, hub: palette.green, hubInner: palette.wheel.armedHubInner, glow: '14px' },
  empty: { rim: palette.wheel.emptyRim, hub: palette.pink.lit, hubInner: palette.wheel.emptyHubInner, glow: '2px' },
} as const;

export const gradients = {
  accountCard: `linear-gradient(180deg,${palette.card.accountTop},${palette.bg.panel})`,
  sourceCard: `linear-gradient(180deg,${palette.card.sourceTop},${palette.card.sourceBottom})`,
  sheet: `linear-gradient(180deg,${palette.card.sheetTop},${palette.card.sheetBottom})`,
  rollerFrame: `linear-gradient(180deg,${palette.roller.frameTop},${palette.roller.frameBottom})`,
  /** Tax-coverage fill, red → green. */
  coverage: `linear-gradient(90deg,${palette.pink.lit},${palette.green})`,
} as const;

// ─── Type ───────────────────────────────────────────────────────────────────

/** The four-font system. Each stack ends in a generic family so a blocked font never renders as nothing. */
export const fonts = {
  /** Display — sparingly (hero type). Local OTFs, see `fonts.css`. */
  display: "'PP Editorial New', Georgia, serif",
  /** Names, sheet titles, voice lines — italic 400–600. */
  serif: "'Cormorant Garamond', Georgia, serif",
  /** Body copy, inputs — 300–400, line-height ~1.7. */
  sans: "'DM Sans', system-ui, sans-serif",
  /** ALL UI scaffolding — UPPERCASE, letter-spaced, 6.5–11px base. */
  mono: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
} as const;

export const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&family=DM+Sans:wght@300;400;500&family=JetBrains+Mono:wght@300;400;500&display=swap';

/** PP Editorial New ships as local OTFs (`design/fonts/`). Product uses 300/400; the marketing tier uses 800. */
export const PP_EDITORIAL_FACES = [
  { weight: 300, style: 'normal', file: 'PPEditorialNew-Ultralight.otf' },
  { weight: 300, style: 'italic', file: 'PPEditorialNew-UltralightItalic.otf' },
  { weight: 400, style: 'normal', file: 'PPEditorialNew-Regular.otf' },
  { weight: 400, style: 'italic', file: 'PPEditorialNew-Italic.otf' },
  { weight: 800, style: 'normal', file: 'PPEditorialNew-Ultrabold.otf' },
  { weight: 800, style: 'italic', file: 'PPEditorialNew-UltraboldItalic.otf' },
] as const;

/**
 * Base type ramp in px. Every size is wrapped in `fs()` at render time
 * (`typeScale.ts`, ×FONT_SCALE) — never hard-code an already-scaled value.
 */
export const typeRamp = {
  tag: 6.5,
  label: 7,
  labelLg: 8,
  section: 9,
  ui: 10,
  uiLg: 11,
  number: 12,
  body: 13,
  bodyLg: 15,
  input: 15,
  inputMono: 16,
  name: 17,
  sheetTitle: 18,
  hero: 26,
} as const;

// ─── Shape, rhythm, motion ──────────────────────────────────────────────────

/** The brand is square. These are the only functional radii, in px. Sheets round the top corners only. */
export const radius = { tag: 3, button: 5, buttonLg: 7, card: 9, sheet: 14 } as const;

export const spacing = {
  sectionPad: 18,
  cardPad: 12,
  cardGap: 12,
  gridGap: 14,
  gridGapY: 16,
  bundleGap: 7,
  valveGap: 10,
} as const;

/** Persistent chrome heights, px. The instrument cluster sizes to content (#237) and is deliberately absent. */
export const chrome = { statusBar: 30, header: 54, modeBar: 40, dock: 56 } as const;

/** No bouncy springs. Restraint. */
export const motion = {
  roll: { ms: 600, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  flow: { ms: 800, easing: 'cubic-bezier(0.5, 0, 0.25, 1)' },
  wheel: { ms: 150, easing: 'linear' },
  general: { ms: 300, easing: 'ease' },
} as const;
